<template>
    <div>
        <vx-table
            v-bind="$attrs"
            v-on="listeners"
            :value="localValue"
            ref="table"
            :striped="striped"
            class="vx-client-side-table"
            :class="{ squished: localValue }"
            :data="indexedData"
            :page-size="pageSize_x"
            :no-data-text="noDataText"
            :hover-flat="disableSelection"
            :paginate="paginate"
            :current-page="currentPage"
            :disabled="disabled"
        >
            <template slot="header">
                <page-size-select
                    v-if="!fixedPageSize && paginate"
                    v-model="pageSize"
                    :disabled="disabled"
                    class="mr-4"
                />

                <slot name="csv">
                    <csv-button
                        v-if="csvReportName"
                        :filters="filters"
                        :endpoint="downloadCsv"
                        :currentResults="indexedData"
                        :totalResults="indexedData.length"
                    />
                </slot>
                <div class="flex flex-grow">
                    <slot name="header-inner" />
                </div>
            </template>

            <template slot="thead">
                <template v-for="(column, colIndex) in columns">
                    <vx-th
                        v-if="column.visible == undefined || column.visible == true"
                        :key="'head-' + colIndex"
                        :sort-key="column.sort"
                    >
                        <slot
                            :name="`${column.slotName || column.title.replace(/\s/g, '')}_head`"
                            :column="column"
                            :col-index="colIndex"
                        >
                            <span :class="column.headerClass" class="w-full whitespace-nowrap">
                                {{ column.title }}
                            </span>
                        </slot>
                    </vx-th>
                </template>
            </template>

            <template slot-scope="{ data }">
                <tbody v-if="!disabled" class="vx-table-body" ref="tbody">
                    <vx-tr
                        v-for="(row, index) in data"
                        class="table-row"
                        :key="getKey(row, index)"
                        :index="index"
                        :data="row"
                        :class="getRowClasses(row)"
                        :ref="row.xIndex"
                        :style="{
                            cursor: disableSelection ? 'default' : 'cursor'
                        }"
                        @click="onTrClick(row, index)"
                    >
                        <slot name="row" :row="row">
                            <template v-for="(column, colIndex) in columns">
                                <vx-td
                                    v-if="column.visible == undefined || column.visible == true"
                                    :key="'col-' + colIndex"
                                    :style="column.style"
                                    :class="getClassesX(row, column)"
                                >
                                    <slot
                                        :name="column.slotName || column.title.replace(/\s/g, '')"
                                        :row="row"
                                        :col-index="colIndex"
                                        :row-index="index"
                                    >
                                        <span>{{ getValue(row, column) }}</span>
                                    </slot>
                                </vx-td>
                            </template>
                        </slot>
                        <template v-slot:expand>
                            <slot name="expand" :row="row" />
                        </template>
                    </vx-tr>
                </tbody>
                <tfoot v-if="$slots['footer']">
                    <slot name="footer" />
                </tfoot>
            </template>
        </vx-table>

        <div class="px-8" v-if="disabled">
            <slot name="disabled">
                <div class="w-full flex justify-center items-center text-warning">
                    <vx-basic-icon color="warning" icon="AlertTriangleIcon" size="8" />
                    <span class="mx-3 my-4 text-warning text-center"> This table is disabled </span>
                    <vx-basic-icon color="warning" icon="AlertTriangleIcon" size="8" />
                </div>
            </slot>
        </div>
    </div>
</template>

<script>
import CsvButton from '../VxServerSideTable/CsvButton.vue'
import PageSizeSelect from '../VxServerSideTable/PageSizeSelect.vue'

export default {
    name: 'vx-client-side-table',
    components: { CsvButton, PageSizeSelect },
    props: {
        value: {
            type: Object
        },
        columns: {
            type: Array,
            required: true
        },
        striped: {
            type: Boolean,
            default: false
        },
        idKey: {
            type: [Function, String],
            default: 'id'
        },
        rowClass: {
            type: [Function, String],
            default: ''
        },
        fixedPageSize: {
            type: [Number, String],
            required: false
        },
        filters: {
            type: Object,
            default: () => {}
        },
        disableSelection: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        data: {
            type: Array,
            required: true
        },
        paginate: {
            type: Boolean,
            default: false
        },
        csvReportName: {
            type: String
        }
    },
    data() {
        return {
            pageSize: 15,
            sortKey: null,
            currentPage: 1,
            filterCount: null,
            localValue: null
        }
    },
    watch: {
        pageSize_x(val) {
            this.$refs.table._data.currentx = this.currentPage
        },
        valueIsVisible: {
            handler: function (newV, oldV) {
                if (newV) {
                    this.$emit('value-shown')
                    return
                }
                this.$emit('value-hidden')
                this.localValue = null
            }
            // immediate: true
        },
        value: {
            handler: function (newV, oldV) {
                if (this.getKey(newV) == this.getKey(oldV)) {
                    return
                }
                this.localValue = newV
            },
            immediate: true
        }
    },
    computed: {
        listeners() {
            var listeners = { ...this.$listeners }
            delete listeners.input
            return listeners
        },
        noDataText() {
            return this.disabled ? '' : 'No Data Available'
        },
        pageSize_x() {
            return this.fixedPageSize || this.pageSize
        },
        valueIsVisible() {
            return this.currentPage == this.pageOfSelectedValue
        },
        valueExistsInDataset() {
            return this.pageOfSelectedValue != -1
        },
        pageOfSelectedValue() {
            if (!this.localValue) {
                return -1
            }

            var index = this.indexedData.indexWhere(
                (v) => this.getKey(v) == this.getKey(this.localValue)
            )

            if (index == -1) {
                return -1
            }
            return Math.floor(index / this.pageSize) + 1
        },
        getKey() {
            if (!this.idKey) {
                return (row, index) => index + this.currentPage * this.pageSize_x
            }
            if (typeof this.idKey === 'function') {
                return this.idKey
            }
            return (row) => row && row[this.idKey]
        },
        indexedData() {
            if (this.disabled || !this.data || this.data.length == 0) {
                return []
            }
            return this.data.map(function (value, index) {
                return { ...value, xIndex: index + 1 }
            })
        },
        totalPages() {
            var current = this.filterCount || this.indexedData.length
            var pageCount = parseInt(current / this.pageSize_x)
            if (parseFloat(current / this.pageSize_x) % 1 != 0) {
                return pageCount + 1
            }
            return pageCount || 1
        },

        getRowClasses() {
            if (!this.rowClass) {
                return (row) => (row == this.value ? 'text-primary' : '')
            }
            if (typeof this.rowClass === 'function') {
                return this.rowClass
            }
            return () => this.rowClass
        }
    },
    methods: {
        onTrClick(row, index) {
            if (this.disableSelection) {
                return
            }
            this.localValue = row
            this.$emit('input', row)
        },
        downloadCsv() {
            return this.$api.downloadJsonToCsv(this.indexedData, this.csvReportName, this.filters)
        },
        getClassesX(row, column) {
            if (!column.class) {
                return ''
            } else if (typeof column.class === 'string') {
                return column.class
            }

            return column.class(row)
        },
        getValue(row, column) {
            var val = ''
            var valueType = column.value && typeof column.value

            if (valueType === 'function') {
                val = column.value(row)
            } else if (valueType === 'string') {
                val = this.retrieveProperty(column.value, row)
            }
            if (column.format) {
                return column.format(val)
            }
            return val
        },
        retrieveProperty(propString, obj) {
            return propString.split('.').reduce((o, i) => o[i], obj)
        }
    },
    created() {
        if (!this.valueIsVisible) {
            this.currentPage = this.pageOfSelectedValue == -1 ? 1 : this.pageOfSelectedValue
        }
    },
    mounted() {
        this.$watch('$refs.table.searchx', (newV, oldV) => {
            this.$emit('search', newV)
            this.currentPage = 1
        })
        this.$watch('$refs.table.currentx', (newV, oldV) => this.$emit('change-page', newV))
        this.$watch(
            '$refs.table.queriedResults',
            (newV, oldV) => (this.filterCount = (newV && newV.length) || 0)
        )
    }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/vuexy/_variables.scss';

.vx-client-side-table {
    height: calc(100% - 54px);

    ::v-deep table {
        background-color: transparent;
        background: none;
    }

    ::v-deep .vs-table--pagination {
        display: none;
    }

    ::v-deep .vs-table--header {
        align-items: end;
        margin-bottom: 1rem;
    }

    ::v-deep .vs-table--search {
        padding: 0 !important;
    }

    ::v-deep .vs-pagination--li {
        font-size: 12px;
    }

    .vx-table-body {
        height: 100%;
        overflow: hidden;
    }

    ::v-deep .vs-table--content {
        flex-grow: 1;
        overflow: hidden !important;
    }

    ::v-deep thead,
    ::v-deep th {
        background-color: inherit;
        position: sticky;
        top: 0;
        z-index: 1;

        .vs-table-text {
            color: rgba(var(--vs-primary), 1) !important;
        }
    }

    ::v-deep .vs-table--tbody {
        overflow: auto;
    }

    &.has-data {
        ::v-deep .vs-table--tbody {
            height: 100%;
            min-height: 200px;
        }
    }

    ::v-deep .vs-tooltip {
        margin-left: 0.6rem !important;
    }
}
</style>
